import * as React from 'react'
import ReactDOMClient from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { NotificationWrapper } from '../../main/components/notification'
import { useCheckNonIdeaChartMissingConfig } from '../../main/hooks/use-check-non-idea-chart-missing-config'
import { initIdeaQuotes } from '../../main/modules/charts/util'
import { Quote } from '../charts-lib'
import { decodeQueryString } from '../queryString'
import { getInitChartConfig } from '../shared/getInitChartConfig'
import { ready } from '../shared/ready'
import { QuoteNavigation } from './Navigation'
import { QuoteChart } from './QuoteChart'
import { QuoteContext, QuoteContextProvider } from './QuoteContext'
import { Perf } from './components/ComparePerformance'
import { InsiderTradingVolumeChart } from './components/InsiderTradingChart'
import { QuotePortal } from './components/Portal'
import { ChartContextQuoteHeader, ManualInitQuoteHeader } from './components/Price'
import Statements from './components/Statements/statements'
import { StaticChartHeader } from './components/StaticChartHeader'
import FundamentalCharts from './components/fundamentalCharts'
import { useStockDetailUpdater } from './hooks/use-stock-detail-updater'
import EtfHoldings from './modules/etf-holdings'
import { QuoteQuery, QuoteSubview, QuoteView } from './types'

if (FinvizSettings.hasUserPremium || FinvizSettings.hasRedesignEnabled) {
  import(/* webpackChunkName: "recent_quotes" */ '../shared/recent_quotes').then(({ RenderRecentQuotes }) => {
    RenderRecentQuotes()
  })
}

const PRICE_STATIC = document.querySelectorAll('.js-quote-price-static')
const PRICE_ROOTS = document.querySelectorAll('.js-quote-price-root')

const NAVIGATION_STATIC = document.querySelectorAll('.js-quote-navigation-static')
const NAVIGATION_ROOTS = document.querySelectorAll('.js-quote-navigation-root')

const ETF_HOLDINGS_STATIC = document.querySelectorAll('.js-etf-holdings-static')
const ETF_HOLDINGS_ROOTS = document.querySelectorAll('.js-etf-holdings-root')
const isETF = !!ETF_HOLDINGS_ROOTS.length

const STATIC_CHART_HEADER_ROOTS = document.querySelectorAll('.quote-menu')

const FUNDAMENTAL_CHARTS_ROOTS = document.querySelectorAll('.js-quote-fa')

const STATEMENTS_ROOTS = document.querySelectorAll('#statements')

const INSIDER_CHART_ROOTS = document.querySelectorAll('.insider-trading-chart')

function ReactComponentsContainer({ isStockDetailUpdating }: { isStockDetailUpdating: boolean }) {
  const context = React.useContext(QuoteContext)

  return (
    <>
      {context.index === 0 && (
        <QuotePortal staticElements={PRICE_STATIC} roots={PRICE_ROOTS} toggleStaticElement={false}>
          <React.Suspense fallback={null}>
            {context.view === QuoteView.ChartDetail ? (
              <ChartContextQuoteHeader />
            ) : (
              <ManualInitQuoteHeader config={window.globalChartConfig} />
            )}
          </React.Suspense>
        </QuotePortal>
      )}

      <QuotePortal staticElements={NAVIGATION_STATIC} roots={NAVIGATION_ROOTS} toggleStaticElement={false}>
        <QuoteNavigation />
      </QuotePortal>

      {context.view === QuoteView.ChartDetail && (
        <React.Suspense fallback={null}>
          {context.index === 0 && <QuoteChart />}

          {context.index > 0 && (
            <QuotePortal
              roots={STATIC_CHART_HEADER_ROOTS}
              // We need to subtract here, because the first ticker doesn’t have this component
              index={context.index - 1}
            >
              <StaticChartHeader isStockDetailUpdating={isStockDetailUpdating} />
            </QuotePortal>
          )}
        </React.Suspense>
      )}

      {context.subview === QuoteSubview.ComparePerf && window.FinvizQuoteTickers && context.index === 0 && <Perf />}

      <QuotePortal staticElements={ETF_HOLDINGS_STATIC} roots={ETF_HOLDINGS_ROOTS}>
        <EtfHoldings />
      </QuotePortal>

      <QuotePortal
        roots={FUNDAMENTAL_CHARTS_ROOTS}
        // Roots for this element don’t render on ETFs
        index={isETF ? context.index - 1 : undefined}
      >
        <FundamentalCharts />
      </QuotePortal>

      <QuotePortal roots={STATEMENTS_ROOTS}>
        <Statements />
      </QuotePortal>

      <QuotePortal roots={INSIDER_CHART_ROOTS}>
        <InsiderTradingVolumeChart className="my-6" />
      </QuotePortal>
    </>
  )
}

function QuoteTickerComponents() {
  useCheckNonIdeaChartMissingConfig()

  const query = decodeQueryString()
  const tickers = query[QuoteQuery.Ticker].split(',')
  const { isStockDetailUpdating } = useStockDetailUpdater()

  return tickers.map((ticker, index) => (
    <QuoteContextProvider key={ticker} ticker={ticker} index={index}>
      <ReactComponentsContainer isStockDetailUpdating={isStockDetailUpdating} />
    </QuoteContextProvider>
  ))
}

ready(() => {
  const reactRootElement = document.getElementById('react-root')

  if (!reactRootElement) return

  if (FinvizSettings.quoteData) {
    Quote.create(FinvizSettings.quoteData)
  }

  if (window.globalChartConfig && !window.globalChartConfig.idea) {
    window.globalChartConfig = getInitChartConfig()

    if (window.globalChartConfig?.ideaQuotes) {
      initIdeaQuotes(window.globalChartConfig.ideaQuotes, window.globalChartConfig)
    }
  }

  ReactDOMClient.createRoot(reactRootElement).render(
    <BrowserRouter>
      <NotificationWrapper>
        <QuoteTickerComponents />
      </NotificationWrapper>
    </BrowserRouter>
  )
})

const revealRatingButtonsCollection = document.getElementsByClassName('js-reveal-ratings-button')
const revealRatingButtons = Array.from(revealRatingButtonsCollection) as HTMLButtonElement[]
revealRatingButtons.forEach((revealRatingButton) => {
  revealRatingButton.onclick = (e) => {
    e.preventDefault()
    const ratingsRowWithTable = revealRatingButton.previousElementSibling

    ratingsRowWithTable?.querySelectorAll('table tr').forEach((rating, index, rows) => {
      if (rating.classList.contains('is-last')) {
        rating.classList.remove('is-last')
      }

      rating.classList.remove('hidden')

      if (index === rows.length - 1) {
        rating.classList.add('is-last')
      }
    })

    revealRatingButton.classList.add('hidden')
    window.gtag && window.gtag('event', 'click', { event_category: 'reveal-ratings' })
  }
})
